"use client";

import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { buildPimaEscalationActionText } from "@/src/lib/customer/scheduling/ushv-pima";
import { useReferralSchedulingRecommendation } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { DiagnosisItem, isStatOrUrgentPriority, Referral, SchedulingPriority } from "@/src/models/documents/referral";
import { getProviderNameWithCredentials } from "@/src/models/practitioner";
import { AgenticRecommendation } from "@/src/models/scheduling-recommendation";

function getEscalationReasonText(agenticRecommendation: AgenticRecommendation | null | undefined): string {
  const receivingProviderEvaluation = agenticRecommendation?.evaluatedPractitioners.find((p) => p.isReceivingProvider);

  return receivingProviderEvaluation?.reasonCodes?.[0] ?? "N/A";
}

function getPrimaryDiagnosisText(referral: Referral): string | null {
  const diagnosis = referral.diagnosisList?.find((d: DiagnosisItem) => d.diagnosisText)?.diagnosisText;

  return diagnosis ?? referral.reasonForReferral ?? null;
}

function getActionPriorityText(referral: Referral): string {
  const priority = referral.schedulingPriority || SchedulingPriority.ROUTINE;

  return isStatOrUrgentPriority(priority) ? "High" : "Normal";
}

function PimaReferralInstructionsContent({ referral }: { referral: Referral }) {
  const referringProviderName = getProviderNameWithCredentials(referral.referringProvider);
  const receivingProviderName = getProviderNameWithCredentials(referral.receivingProvider);
  const diagnosis = getPrimaryDiagnosisText(referral);
  const assignedTo = "CSO,PIMA Referral Management";
  const priority = getActionPriorityText(referral);
  const { recommendation } = useReferralSchedulingRecommendation(referral.id);

  const appointmentReason = `Referred by Dr ${referringProviderName} for ${referral.reasonForReferral || "referral"}`;
  const escalationReason = getEscalationReasonText(recommendation?.agenticRecommendation);
  const { subject: actionSubject, note: actionNotes } = buildPimaEscalationActionText(
    escalationReason,
    `Referred to Dr ${receivingProviderName} by Dr ${referringProviderName}.`
  );

  return (
    <div className="flex flex-col gap-4">
      <span className="font-medium text-center">Pima Referral Instructions:</span>
      <div className="text-muted-foreground space-y-4">
        <ReferralSchedulingRecommendation referral={referral} title="Scheduling Recommendation:" />

        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">Appointment Details:</span>
          <div className="ml-4 space-y-1">
            <div>
              <span className="font-medium">Reason:</span> <CopyableContent text={appointmentReason} />
            </div>
            <div>
              <span className="font-medium">Diagnosis:</span> <CopyableContent text={diagnosis || "N/A"} />
            </div>
            <div>
              <span className="font-medium">General Notes:</span>{" "}
              <CopyableContent text="Scheduled via SMS by Valerie Health" />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">Action Details:</span>
          <div className="ml-4 space-y-1">
            <div>
              <span className="font-medium">Subject:</span> <CopyableContent text={actionSubject} />
            </div>
            <div>
              <span className="font-medium">Assigned to:</span> <CopyableContent text={assignedTo} />
            </div>
            <div>
              <span className="font-medium">Due date:</span> <CopyableContent text="Today" />
            </div>
            <div>
              <span className="font-medium">Status:</span> <CopyableContent text="Not Started" />
            </div>
            <div>
              <span className="font-medium">Priority:</span> <CopyableContent text={priority} />
            </div>
            <div>
              <span className="font-medium">Notes:</span> <CopyableContent text={actionNotes} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PimaReferralCards({ referral }: { referral: Referral }) {
  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </>
  );
}

interface PimaReferralInstructionsProps {
  referral: Referral;
}

export function PimaReferralInstructions({ referral }: PimaReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <PimaReferralInstructionsContent referral={referral} />
        </div>
      </EhrInstructionsCard>

      <PimaReferralCards referral={referral} />
    </div>
  );
}
